import { useState, useEffect, useCallback } from 'react'
import { Table, Button, Space, Input, Modal, Form, Tag, Typography, message, Select } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import '../../styles/aurora-page.css'
import '../../styles/aurora-modal.css'
import { getProjects, createProject, updateProject, deleteProject, getProjectAssets } from '@/api/projects'
import { listUsers } from '@/api/users'
import { useProjectStore } from '@/stores/project'
import { useAuthStore } from '@/stores/auth'
import type { Project } from '@/types'

const { Title, Text } = Typography

interface ProjectFormValues {
  name: string
  key: string
  description: string
  members: number[]
  status: 'developing' | 'online' | 'archived'
}

export default function ProjectsPage() {
  const [data, setData] = useState<Project[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 })
  const [modalOpen, setModalOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm<ProjectFormValues>()

  const { user } = useAuthStore()
  const { fetchProjects } = useProjectStore()

  const isAdmin = user?.role === 1

  /** 全部用户（供成员选择） */
  const [users, setUsers] = useState<{ id: number; username: string; nickname: string }[]>([])

  const userLabel = useCallback(
    (id: number) => {
      const u = users.find((x) => x.id === id)
      return u ? (u.nickname || u.username) : `用户 #${id}`
    },
    [users],
  )

  const userOptions = users.map((u) => ({
    value: u.id,
    label: u.nickname || u.username,
  }))

  const fetchUsers = useCallback(async () => {
    try {
      const res = await listUsers({ page: 1, page_size: 100 })
      setUsers(res.data.map((u) => ({ id: u.id, username: u.username, nickname: u.nickname || '' })))
    } catch {
      // 错误已在拦截器处理
    }
  }, [])

  const fetchData = useCallback(async (page = 1, pageSize = 10, keyword = '') => {
    setLoading(true)
    try {
      const res = await getProjects({ page, page_size: pageSize, keyword })
      setData(res.data)
      setPagination((prev) => ({ ...prev, page, pageSize, total: res.total }))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
    fetchUsers()
  }, [fetchData, fetchUsers])

  /** 新增项目 */
  const handleCreate = () => {
    setEditingProject(null)
    form.resetFields()
    form.setFieldsValue({ status: 'developing', members: [] })
    setModalOpen(true)
  }

  /** 编辑 */
  const handleEdit = (record: Project) => {
    setEditingProject(record)
    form.setFieldsValue({
      name: record.name,
      key: record.key,
      description: record.description,
      members: record.members || [],
      status: record.status,
    })
    setModalOpen(true)
  }

  /** 删除 */
  const handleDelete = async (id: number) => {
    try {
      await deleteProject(id)
      message.success('删除成功')
      fetchData(pagination.page, pagination.pageSize)
      fetchProjects()
    } catch {
      // 错误已在拦截器处理
    }
  }

  /** 资产盘点弹窗：删除前盘点项目下数据，有数据则列举禁止删除 */
  const [assetsModal, setAssetsModal] = useState<{
    open: boolean
    name: string
    items: { label: string; count: number }[]
  }>({ open: false, name: '', items: [] })

  const handleDeleteClick = async (record: Project) => {
    try {
      const res = await getProjectAssets(record.id)
      if (res.has_data) {
        setAssetsModal({ open: true, name: record.name, items: res.items })
        return
      }
      Modal.confirm({
        title: `确定删除项目「${record.name}」？`,
        content: '项目下暂无任何数据资产，删除后可在数据中恢复（软删除）',
        okText: '删除',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: () => handleDelete(record.id),
      })
    } catch {
      // 错误已在拦截器处理
    }
  }

  /** 提交表单 */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)
      if (editingProject) {
        await updateProject(editingProject.id, values)
        message.success('更新成功')
      } else {
        await createProject({ ...values, owner_id: user?.id || 1 })
        message.success('创建成功')
      }
      setModalOpen(false)
      fetchData(pagination.page, pagination.pageSize)
      fetchProjects()
    } catch {
      // 表单校验失败或接口错误
    } finally {
      setSubmitting(false)
    }
  }

  const columns: ColumnsType<Project> = [
    {
      title: '项目名称',
      dataIndex: 'name',
      render: (name: string, record) => (
        <Space>
          <span
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: record.color || 'var(--au-primary)',
            }}
          />
          <span style={{ fontWeight: 500 }}>{name}</span>
        </Space>
      ),
    },
    { title: '项目标识', dataIndex: 'key', width: 120 },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '项目成员',
      dataIndex: 'members',
      width: 260,
      render: (members: number[] = []) => {
        if (!members.length) return <span style={{ color: 'var(--au-slate-muted)' }}>-</span>
        const shown = members.slice(0, 3)
        const rest = members.length - shown.length
        return (
          <Space size={[4, 4]} wrap>
            {shown.map((id) => (
              <Tag key={id}>{userLabel(id)}</Tag>
            ))}
            {rest > 0 && <Tag>+{rest}</Tag>}
          </Space>
        )
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: string) => {
        const map: Record<string, { label: string; color: string }> = {
          developing: { label: '开发中', color: 'processing' },
          online: { label: '已上线', color: 'success' },
          archived: { label: '归档维护', color: 'warning' },
        }
        const cfg = map[status] || { label: '未知', color: 'default' }
        return <Tag color={cfg.color}>{cfg.label}</Tag>
      },
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      width: 200,
      render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-',
    },
    // 新增/编辑/删除项目仅管理员可用
    ...(isAdmin
      ? [{
          title: '操作',
          width: 160,
          render: (_: unknown, record: Project) => (
            <Space size="small">
              <Button type="link" size="small" onClick={() => handleEdit(record)}>
                编辑
              </Button>
              <Button type="link" size="small" danger onClick={() => handleDeleteClick(record)}>
                删除
              </Button>
            </Space>
          ),
        }]
      : []),
  ]

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          项目管理
        </Title>
        <Space>
          <Button onClick={() => fetchData(1, pagination.pageSize)}>
            刷新
          </Button>
          {isAdmin && (
            <Button type="primary" onClick={handleCreate}>
              新增项目
            </Button>
          )}
        </Space>
      </div>

      <div className="search-bar">
        <Space.Compact>
          <Input
            placeholder="搜索项目名称或标识"
            allowClear
            onPressEnter={(e) => { const v = (e.target as HTMLInputElement).value; fetchData(1, pagination.pageSize, v) }}
            style={{ minWidth: 200 }}
          />
          <Button
            type="primary"
            onClick={() => {
              const input = document.querySelector('.search-bar input') as HTMLInputElement | null
              const val = input?.value || ''
              fetchData(1, pagination.pageSize, val)
            }}
            style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}
          >
            查询
          </Button>
        </Space.Compact>
      </div>

      <div className="content-card">
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ y: '100%' }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => fetchData(page, pageSize),
          }}
        />
      </div>

      <Modal
        title={editingProject ? '编辑项目' : '新增项目'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        destroyOnClose
        wrapClassName="aurora-modal-wrap"
        className="aurora-modal"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 4 }}>
          <Form.Item
            name="name"
            label="项目名称"
            rules={[{ required: true, message: '请输入项目名称' }]}
          >
            <Input placeholder="请输入项目名称" />
          </Form.Item>
          <Form.Item
            name="key"
            label="项目标识"
            rules={[
              { required: true, message: '请输入项目标识' },
              { min: 2, max: 32, message: '2-32 个字符' },
              { pattern: /^[A-Z0-9_]+$/, message: '仅允许大写字母、数字和下划线' },
            ]}
          >
            <Input placeholder="如: FTUSC" disabled={!!editingProject} />
          </Form.Item>
          <Form.Item name="description" label="项目描述">
            <Input.TextArea rows={3} placeholder="请输入项目描述" />
          </Form.Item>
          <Form.Item name="members" label="项目成员">
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              options={userOptions}
              placeholder="从用户列表选择项目成员（可多选）"
            />
          </Form.Item>
          <Form.Item
            name="status"
            label="项目状态"
            rules={[{ required: true, message: '请选择项目状态' }]}
          >
            <Select
              options={[
                { value: 'developing', label: '开发中' },
                { value: 'online', label: '已上线' },
                { value: 'archived', label: '归档维护' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 项目资产盘点弹窗：存在数据时禁止删除并列举资产 */}
      <Modal
        title={`项目「${assetsModal.name}」存在数据资产，无法删除`}
        open={assetsModal.open}
        onCancel={() => setAssetsModal((s) => ({ ...s, open: false }))}
        footer={[
          <Button key="ok" type="primary" onClick={() => setAssetsModal((s) => ({ ...s, open: false }))}>
            知道了
          </Button>,
        ]}
        wrapClassName="aurora-modal-wrap"
        className="aurora-modal"
      >
        <Text type="secondary">请先清理该项目下的以下数据后再删除：</Text>
        <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {assetsModal.items.map((it) => (
            <Tag key={it.label} color="orange">
              {it.label} · {it.count} 条
            </Tag>
          ))}
        </div>
      </Modal>
    </div>
  )
}
